// Locates a diff phrase inside the pour editors and highlights it. Ranges are painted with the CSS
// Custom Highlight API — no <mark> injection, so nothing dirties the editable content or the
// copied HTML (browsers without the API still get the scroll, just no highlight). Matching goes
// through the same canonicalizations the backend Normalizer applies (smart quotes, ellipsis,
// nbsp, zero-width characters, collapsed whitespace), since diff text is normalized.

interface CharRef {
  node: Text;
  offset: number;
}

export interface LocateContext {
  after?: string | null;
  before?: string | null;
}

export interface LocatePane extends LocateContext {
  phrase: string | null;
  root: HTMLElement | null;
}

const HIGHLIGHT_NAME = 'pour-locate';
const ZERO_WIDTH = /[\u200B-\u200D\u2060\uFEFF\u00AD]/;

const normalizeChar = (raw: string): string | null => {
  if (ZERO_WIDTH.test(raw)) return null;
  if (raw === '\u201C' || raw === '\u201D') return '"';
  if (raw === '\u2018' || raw === '\u2019') return "'";
  if (raw === '\u2026') return '...';
  if (/\s/.test(raw) || raw === '\u00A0') return ' ';
  return raw;
};

const normalizePhrase = (phrase: string) => phrase
  .split('')
  .map(normalizeChar)
  .filter(Boolean)
  .join('')
  .replace(/ +/g, ' ')
  .trim();

// Walks the editor's text nodes into one normalized string with a per-character map back to the DOM.
const collectText = (root: HTMLElement) => {
  const chars: string[] = [];
  const refs: CharRef[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node) {
    const text = node as Text;
    for (let i = 0; i < text.data.length; i += 1) {
      const norm = normalizeChar(text.data[i]);
      const skip = norm === null || (norm === ' ' && (chars.length === 0 || chars[chars.length - 1] === ' '));
      if (!skip) {
        Array.from(norm as string).forEach((ch) => {
          chars.push(ch);
          refs.push({ node: text, offset: i });
        });
      }
    }
    node = walker.nextNode();
  }
  return { refs, text: chars.join('') };
};

const rangeFor = (refs: CharRef[], start: number, length: number): Range => {
  const first = refs[start];
  const last = refs[start + length - 1];
  // new Range(), not document.createRange() — the jest setup stubs the latter for Tippy.
  const range = new Range();
  range.setStart(first.node, first.offset);
  range.setEnd(last.node, last.offset + 1);
  return range;
};

// Finds the phrase and returns a Range over its original characters. A short phrase alone is
// ambiguous ("own" appears many times), so when the diff's neighboring context words are given,
// context-anchored matches are tried first — the returned range still covers only the phrase.
// Context that spans a block boundary won't match (blocks glue without a space), so each side is
// also tried alone before falling back to the bare phrase.
export const findPhrase = (root: HTMLElement, phrase: string, context: LocateContext = {}): Range | null => {
  const needle = normalizePhrase(phrase);
  if (!needle) return null;

  const { refs, text } = collectText(root);
  const before = normalizePhrase(context.before || '');
  const after = normalizePhrase(context.after || '');

  const candidates: { needleStart: number; searched: string }[] = [];
  if (before && after) candidates.push({ needleStart: before.length + 1, searched: `${before} ${needle} ${after}` });
  if (before) candidates.push({ needleStart: before.length + 1, searched: `${before} ${needle}` });
  if (after) candidates.push({ needleStart: 0, searched: `${needle} ${after}` });
  candidates.push({ needleStart: 0, searched: needle });

  const start = candidates
    .map(({ needleStart, searched }) => {
      const found = text.indexOf(searched);
      return found === -1 ? null : found + needleStart;
    })
    .find(found => found !== null);

  return start == null ? null : rangeFor(refs, start, needle.length);
};

interface HighlightRegistry {
  set: (name: string, highlight: unknown) => void;
}

// The highlight persists until another change is located (this replaces the whole registry
// entry), so a reviewer can keep the words marked while reading around them or editing.
const paint = (ranges: Range[]) => {
  const registry = (CSS as unknown as { highlights?: HighlightRegistry }).highlights;
  const HighlightCtor = (window as unknown as { Highlight?: new (...highlighted: Range[]) => unknown }).Highlight;
  if (!registry || !HighlightCtor) return;

  registry.set(HIGHLIGHT_NAME, new HighlightCtor(...ranges));
};

// One pane per editor (draft gets the print text, current gets the online text). Every located
// range scrolls ITS OWN editor (each Froala wrapper is a scroll container) and stays highlighted;
// the window's position follows the last call, so callers order panes least→most preferred (the
// current-online editor last). Returns whether anything was found.
export const highlightChange = (panes: LocatePane[]): boolean => {
  const ranges = panes
    .map(({ after, before, phrase, root }) => (phrase && root ? findPhrase(root, phrase, { after, before }) : null))
    .filter((range): range is Range => range !== null);
  if (ranges.length === 0) return false;

  ranges.forEach((range) => {
    range.startContainer?.parentElement?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  });
  paint(ranges);
  return true;
};
